import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import AnalyticsService from '../helpers/analytics.service';
import { EVENTS } from 'shared/constants';
import { getPageTitle, getAnalyticsPayload } from '../utils/page-title-mapper';
import { getSourceFromRoute } from 'shared/helpers/src/navigation-source-mapper';
import navigationContextManager from 'shared/helpers/src/navigation-context-manager';

/**
 * Custom hook that tracks page views automatically
 * Similar to the mobile app's navigation tracking in App.navigation.js
 *
 * @param {string} [customPageName] - Optional custom page name to override the pathname
 * @param {object} [customPayload] - Optional custom payload to include with the tracking event
 */
const useNavigationTracker = (customPageName, customPayload = {}) => {
  const location = useLocation();
  const previousPathnameRef = useRef();
  const previousPayloadRef = useRef();

  useEffect(() => {
    const currentPathname = location.pathname;
    const previousPathname = previousPathnameRef.current;
    const currentSearch = location.search;

    // Parse query params for routes that use them (e.g., /rotations?id=123)
    const queryParams = new URLSearchParams(currentSearch);
    const params = Object.fromEntries(queryParams.entries());

    // Preserve context when navigating to a child route (e.g., /hub → /hub/items/123)
    // This ensures sourceId from parent route is available for analytics on child pages
    // Exclude root path "/" since it would match all routes
    const isChildRoute =
      previousPathname &&
      previousPathname !== '/' &&
      currentPathname.startsWith(previousPathname) &&
      currentPathname !== previousPathname;

    if (!isChildRoute) {
      navigationContextManager.clearContext();
    }

    // Auto-set source context based on route (overrides preserved context if mapped)
    const sourceContext = getSourceFromRoute(currentPathname, params);
    if (sourceContext) {
      navigationContextManager.setContext(sourceContext);
    }

    // Get the analytics payload for this route
    const analyticsPayload = getAnalyticsPayload(currentPathname, currentSearch);

    // Use custom page name if provided, otherwise use the mapped page title
    const pageName = customPageName || getPageTitle(currentPathname, currentSearch);

    // If customPageName is provided, use default payload; otherwise use the analytics payload
    const payload = customPageName ? customPayload : analyticsPayload;

    // Compare with previous payload to determine if we should track
    const previousPayload = previousPayloadRef.current;
    const payloadChanged = JSON.stringify(previousPayload) !== JSON.stringify(payload);
    const pathnameChanged = previousPathname !== currentPathname;

    // Only track if pathname changed OR if payload changed (meaningful data changed)
    if (pathnameChanged || payloadChanged) {
      AnalyticsService.track(EVENTS.APPLICATION.PAGE_VIEW, {
        name: pageName,
        ...payload,
      });
    }

    previousPathnameRef.current = currentPathname;
    previousPayloadRef.current = payload;
  }, [location, customPageName, customPayload]);

  // Return an object with useful navigation information if needed
  return {
    pathname: location.pathname,
    search: location.search,
  };
};

export default useNavigationTracker;
